import Dialog from '@mui/material/Dialog';
import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  InputLabel,
  Snackbar,
  TextareaAutosize,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { secondary } from '../../constants/theme/colors.tsx';
import { HomeInquiryRequest } from '../../models/panorama.models';
import { useMutateHomeInquiry } from '../../api/panorama';
import { useGetCustomer } from '../../api/contact';
import './HomeInquiryModal.scss';

interface HomeInquiryModalProps {
  savedHomeDesign: boolean;
  homeId: string;
}

export const HomeInquiryModal = (props: HomeInquiryModalProps) => {
  const { savedHomeDesign, homeId } = props;

  const [tellUsMore, setTellUsMore] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: userAttributes } = useGetCustomer();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const labelStyle = {
    color: 'white',
    fontSize: '12px',
    marginBottom: '4px',
  };

  const { mutate, isPending } = useMutateHomeInquiry();

  const handleSubmit = () => {
    if (!tellUsMore.trim()) {
      setErrorMessage('Please fill out the "Tell us more" field.');
      setShowErrorSnackbar(true);
      return;
    }

    const req: HomeInquiryRequest = {
      contactPreference: userAttributes?.contactPreferences || '',
      customerText: tellUsMore,
      homeDesignLink: homeId,
    };

    mutate(req, {
      onSuccess: () => {
        setIsOpen(false);
        setShowSuccessSnackbar(true);
      },
      onError: () => {
        setErrorMessage('An error occurred, please contact support');
        setShowErrorSnackbar(true);
      },
    });
  };

  return (
    <>
      <Tooltip
        title={
          !savedHomeDesign
            ? 'Save your design to enable ordering of your home'
            : ''
        }
      >
        <Button
          style={
            !savedHomeDesign
              ? {
                  position: 'absolute',
                  top: `0px`,
                  right: '0px',
                  borderRadius: 0,
                  zIndex: 1,
                  backgroundColor: secondary.light,
                  color: 'lightgrey',
                  cursor: 'auto',
                }
              : {
                  position: 'absolute',
                  top: `0px`,
                  right: '0px',
                  borderRadius: 0,
                  zIndex: 1,
                }
          }
          onClick={() => {
            if (savedHomeDesign) setIsOpen(true);
          }}
        >
          Home Inquiry
        </Button>
      </Tooltip>
      <Dialog
        className="auth-dialog"
        fullScreen={fullScreen}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        aria-labelledby="home-inquiry-dialog-title"
      >
        <Box
          className="container"
          sx={{ minWidth: { xs: 'initial', sm: '600px' } }}
        >
          <Box className="header">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Button
                onClick={() => setIsOpen(false)}
                sx={{ color: 'white' }}
                variant="transparent"
              >
                <CloseIcon />
              </Button>
            </Box>

            <DialogTitle
              sx={{ padding: '12px', fontSize: '45px', textAlign: 'center' }}
              id="share-dialog-title"
            >
              Home Inquiry
            </DialogTitle>
          </Box>
          <DialogContent className="content">
            <InputLabel sx={labelStyle}>Action requested</InputLabel>
            <TextField
              fullWidth
              className="text-input"
              disabled
              value="Home Inquiry"
            />
            <Box className="field-group">
              <Box className="field">
                <InputLabel sx={labelStyle}>First Name</InputLabel>
                <TextField
                  fullWidth
                  className="text-input"
                  disabled
                  value={userAttributes?.firstName}
                />
              </Box>

              <Box className="field">
                <InputLabel sx={labelStyle}>Last Name</InputLabel>
                <TextField
                  fullWidth
                  className="text-input"
                  disabled
                  value={userAttributes?.lastName}
                />
              </Box>
            </Box>

            <Box className="field-group">
              <Box className="field">
                <InputLabel sx={labelStyle}>Cell Phone</InputLabel>
                <TextField
                  fullWidth
                  className="text-input"
                  disabled
                  value={userAttributes?.phoneNumber}
                />
              </Box>

              <Box className="field">
                <InputLabel sx={labelStyle}>Contact Preference</InputLabel>
                <TextField
                  fullWidth
                  className="text-input"
                  disabled
                  value={userAttributes?.contactPreferences}
                />
              </Box>
            </Box>

            <Box className="field">
              <InputLabel sx={labelStyle}>Anything Else?</InputLabel>
              <TextareaAutosize
                onChange={(e) => setTellUsMore(e.target.value)}
                className="text-box"
                placeholder="Tell us more here!"
              />
            </Box>
            <Button
              disabled={isPending || !tellUsMore.trim()}
              onClick={() => handleSubmit()}
              sx={{
                width: '100%',
                marginTop: '12px',
                background: 'white',
                textTransform: 'capitalize',
              }}
              variant="outlined"
            >
              {isPending ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogContent>
        </Box>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={() => setShowSuccessSnackbar(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Request Sent!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={() => setShowErrorSnackbar(false)}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
